import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useSwipeable } from 'react-swipeable';
import { threePracticaLocales } from '../../locales/firstScreen';
import { Footer } from '../UI/Footer/Footer';

const LANG_KEY = 'stonpools-lang';

interface ThreePractica4Props {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeUp?: () => void;
  toHome?: () => void;
  handleDown?: () => void;
}

const slideVariants = {
  enter: {
    x: '100%',
  },
  center: {
    x: 0,
  },
  exit: {
    x: '-100%',
  },
};

const Container = styled.div`
  background: #151515;
  width: 100%;
  height: 100vh;
  padding: 0;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
  padding-bottom: 100px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  width: 100%;
  margin: 50px auto 30px;
  padding: 13px;
  border-radius: 20px;
  justify-content: center;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url(/assets/background.gif) center/cover no-repeat;
    mix-blend-mode: normal;
    opacity: 0.9;
    border-radius: 20px;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: #7354f2;
    opacity: 1;
    mix-blend-mode: multiply;
    border-radius: 20px;
  }

  > * {
    position: relative;
    z-index: 2;
  }
`;

const Title = styled.h2`
  color: #ffffff;
  font-family: 'Manrope', sans-serif;
  font-size: 42px;
  margin-bottom: 15px;
  margin-top: min(1px, 0.2vh);
  line-height: 80%;
  font-weight: 600;
  text-align: center;
`;

const PracticaButton = styled.button`
  background: none;
  border: 2px solid #ffffff;
  border-radius: 12px;
  padding: 5px 7px;
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  text-align: left;
  line-height: 100%;
`;

const Content = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  padding: 0 10px;
`;
const Image = styled.img`
  width: 100%;
  max-width: 300px;
  height: auto;
  object-fit: contain;
`;

const Text = styled.p`
  color: #b4b4b4;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  text-align: center;
  margin: 0 17px;
  line-height: 145%;
  flex-shrink: 0;
`;

const QuestionBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  position: relative;
  width: fit-content;
`;

const QuestionText = styled.span`
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: clamp(26px, 5vw, 26px);
  font-weight: 600;
  line-height: 90%;
  letter-spacing: -0.02em;
  text-align: center;
  white-space: pre-line;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  flex: 1;
  margin: auto 0;
  padding: 20px 0;
`;

const HighlightBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin: 16px auto 20px;
  aspect-ratio: 3/1;
  box-sizing: border-box;
  max-width: 251px;
`;

const HighlightText = styled.span`
  color: #7354f2;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 600;
  text-align: center;
  line-height: 1.4;
  text-transform: uppercase;
  text-shadow:
    0 0 5px rgba(115, 84, 242, 0.4),
    0 0 10px rgba(115, 84, 242, 0.3),
    0 0 15px rgba(115, 84, 242, 0.2),
    0 0 20px rgba(115, 84, 242, 0.15),
    0 0 25px rgba(115, 84, 242, 0.1);
`;

const ScrollIcon = styled.img`
  width: clamp(16px, 5vw, 20px);
  height: clamp(32px, 8vh, 41px);
  margin: clamp(20px, 4vh, 60px) auto 20px;
  display: block;
  filter: hue-rotate(40deg) saturate(120%);

  @media screen and (max-height: 568px) {
    margin: 16px auto;
  }
`;

const HomeButtonIcon = styled.img`
  width: 22px;
  height: 22px;
  object-fit: contain;
`;

const HomeButton = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  margin: 0;
  background-color: transparent;
`;

const ThreePractica4: React.FC<ThreePractica4Props> = ({
  onSwipeUp,
  onSwipeRight,
  onSwipeLeft,
  toHome,
}) => {
  const handlers = useSwipeable({
    onSwipedUp: () => {
      onSwipeUp?.();
    },
    onSwipedRight: () => {
      onSwipeRight?.();
    },
    onSwipedLeft: () => {
      onSwipeLeft?.();
    },
    trackMouse: true,
    delta: 50,
    swipeDuration: 500,
    preventScrollOnSwipe: true,
  });

  const lang = (localStorage.getItem(LANG_KEY) as 'ru' | 'en') || 'ru';
  const t = threePracticaLocales[lang][4];

  return (
    <motion.div
      initial="enter"
      animate="center"
      exit="exit"
      variants={slideVariants}
      transition={{ duration: 0.3 }}
      {...handlers}
    >
      <Container>
        <HomeButton onClick={toHome}>
          <HomeButtonIcon src="/assets/homeIconPurple.svg" />
        </HomeButton>
        <Header>
          <Title>{t.title}</Title>
          <PracticaButton>{t.practica}</PracticaButton>
        </Header>
        <QuestionBlock>
          <QuestionText>{t.question}</QuestionText>
        </QuestionBlock>
        <Content>
          <Text>{t.description}</Text>
          <ImageContainer>
            <Image src="/assets/purplcake.svg" alt="LP Tokens" />
          </ImageContainer>
          <HighlightBox>
            <HighlightText>{t.highlight}</HighlightText>
          </HighlightBox>
          <Footer textRight={t.next} onSwipeLeft={onSwipeLeft} />
        </Content>
      </Container>
    </motion.div>
  );
};

export default ThreePractica4;
